/**
 * Typed access to the fee parameters, with provenance attached.
 *
 * Every accessor returns the figure together with its `Citation`, so a caller
 * physically cannot read a rate without also holding the source and the
 * `verified` flag. That is the mechanism behind the product rule that an
 * unconfirmed figure is never stated as fact.
 *
 * SBA sets 7(a) fees per federal fiscal year, which begins 1 October. A loan is
 * priced by the schedule in force on its approval date, so selecting the right
 * schedule is part of being accurate — using last year's schedule is the single
 * most common error in public calculators.
 */

import feesJson from '../data/fees-7a.json';
import type { Citation, Cited, LoanType } from './types.js';

export interface MarginalBand {
  /** Guaranteed-dollar ceiling for this band, or null for "everything above". */
  upToGuaranteed: number | null;
  pct: number;
}

export interface GrossTier {
  /** Gross-approval-amount ceiling that selects this tier, or null for the top. */
  maxGrossAmount: number | null;
  marginal: MarginalBand[];
}

export interface MaturityTier {
  maxMaturityMonths: number;
  pct: number;
}

export interface ManufacturerWaiver {
  naicsSectors: string[];
  maxGrossAmount: number;
  pct: number;
  excludesMarc: boolean;
}

export interface NinetyDayRule {
  windowDays: number;
  includesAffiliates: boolean;
  combinesForGuarantyPct: boolean;
  combinesForUpfrontFee: boolean;
  feeNeverNegative: boolean;
  wcpEwcpExcludedFromFeeCombination: boolean;
}

/** A fiscal-year entry as it appears in the data file. */
interface RawSchedule {
  fiscalYear: number;
  effective: string;
  expires: string;
  source: string;
  citation: string | null;
  asOf: string;
  verified: boolean;
  implemented?: boolean;
  _verifyNote?: string;
  annualServiceFeePct?: number;
  longTermTiers?: GrossTier[];
  shortTermPct?: number;
  shortTermMaxMaturityMonths?: number;
  wcpTiers?: MaturityTier[];
  ewcpTiers?: MaturityTier[];
  waivers?: {
    manufacturer?: ManufacturerWaiver;
    veteranExpress?: { pct: number; statute: string };
  };
  ninetyDayRule?: NinetyDayRule;
}

/** A schedule with every rate present, so the engine can price with it. */
export interface FeeSchedule extends RawSchedule {
  annualServiceFeePct: number;
  longTermTiers: GrossTier[];
  shortTermPct: number;
  shortTermMaxMaturityMonths: number;
  wcpTiers: MaturityTier[];
  ewcpTiers: MaturityTier[];
  waivers: {
    manufacturer: ManufacturerWaiver;
    veteranExpress: { pct: number; statute: string };
  };
  ninetyDayRule: NinetyDayRule;
}

const DATA = feesJson as unknown as {
  guarantyPct: {
    source: string;
    citation: string;
    asOf: string;
    verified: boolean;
    _verifyNote?: string;
    standard: Array<{ maxGrossAmount: number | null; pct: number }>;
    exportExpress: Array<{ maxGrossAmount: number | null; pct: number }>;
    express: number;
    internationalTrade: number;
    ewcp: number;
    maxGuarantyAmount: number;
    exportMaxGuarantyAmount: number;
    ninetyDayCombinedThreshold: number;
    ninetyDayCombinedMaxPct: number;
  };
  exposureCaps: {
    source: string;
    citation: string;
    asOf: string;
    verified: boolean;
    _verifyNote?: string;
    maxGrossLoanAmount: number;
    maxTotalSbaGuarantyIncluding504: number;
  };
  fiscalYears: RawSchedule[];
};

function citationOf(x: {
  source: string;
  citation: string | null;
  asOf: string;
  verified: boolean;
  _verifyNote?: string;
}): Citation {
  return {
    source: x.source,
    citation: x.citation,
    asOf: x.asOf,
    verified: x.verified,
    ...(x._verifyNote ? { verifyNote: x._verifyNote } : {}),
  };
}

/** True when a schedule carries every rate the engine needs. */
function isComplete(s: RawSchedule): s is FeeSchedule {
  return (
    s.verified &&
    s.implemented !== false &&
    typeof s.annualServiceFeePct === 'number' &&
    typeof s.shortTermPct === 'number' &&
    typeof s.shortTermMaxMaturityMonths === 'number' &&
    Array.isArray(s.longTermTiers) &&
    Array.isArray(s.wcpTiers) &&
    Array.isArray(s.ewcpTiers) &&
    s.waivers?.manufacturer !== undefined &&
    s.waivers?.veteranExpress !== undefined &&
    s.ninetyDayRule !== undefined
  );
}

/**
 * The federal fiscal year an approval date falls in. FY2027 runs from
 * 2026-10-01 to 2027-09-30, so October onward belongs to the next year.
 */
export function fiscalYearFor(isoDate: string): number {
  const m = /^(\d{4})-(\d{2})/.exec(isoDate);
  if (!m) throw new Error(`approvalDate must be an ISO date, got "${isoDate}"`);
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12) throw new Error(`invalid month in "${isoDate}"`);
  return month >= 10 ? year + 1 : year;
}

export interface SelectedSchedule {
  schedule: FeeSchedule;
  /** True when this is not the schedule governing the approval date. */
  provisional: boolean;
  /** Why it is provisional, in the voice shown to the borrower. */
  notes: string[];
}

/**
 * The schedule to price with.
 *
 * When the governing fiscal year has no verified schedule yet, this falls back
 * to the most recent verified one and says so rather than refusing: a borrower
 * is better served by a figure labelled "based on last year's published
 * schedule" than by no figure at all. What it must never do is present that
 * figure as the current fee.
 */
export function scheduleFor(approvalDate: string): SelectedSchedule {
  const fy = fiscalYearFor(approvalDate);
  const governing = DATA.fiscalYears.find((s) => s.fiscalYear === fy);

  if (governing && isComplete(governing)) {
    return { schedule: governing, provisional: false, notes: [] };
  }

  const fallback = DATA.fiscalYears
    .filter(isComplete)
    .sort((a, b) => b.fiscalYear - a.fiscalYear)[0];

  if (!fallback) {
    throw new Error('No verified fee schedule in calculator/data/fees-7a.json');
  }

  const reason = governing
    ? `SBA's fee notice for fiscal year ${fy} has not been confirmed from a primary source yet`
    : `no fee schedule for fiscal year ${fy} is on file`;

  return {
    schedule: fallback,
    provisional: true,
    notes: [
      `This fee is an estimate: ${reason}, so the published fiscal year ${fallback.fiscalYear} schedule was used instead. SBA resets 7(a) fees every 1 October, so your actual fee may differ. Ask your lender for the current figure.`,
    ],
  };
}

/** Guaranty percentage parameters, with their citation. */
export function guarantyPctParams(): Cited<{
  standard: Array<{ maxGrossAmount: number | null; pct: number }>;
  exportExpress: Array<{ maxGrossAmount: number | null; pct: number }>;
  express: number;
  internationalTrade: number;
  ewcp: number;
  maxGuarantyAmount: number;
  exportMaxGuarantyAmount: number;
  ninetyDayCombinedThreshold: number;
  ninetyDayCombinedMaxPct: number;
}> {
  const g = DATA.guarantyPct;
  return {
    value: {
      standard: g.standard,
      exportExpress: g.exportExpress,
      express: g.express,
      internationalTrade: g.internationalTrade,
      ewcp: g.ewcp,
      maxGuarantyAmount: g.maxGuarantyAmount,
      exportMaxGuarantyAmount: g.exportMaxGuarantyAmount,
      ninetyDayCombinedThreshold: g.ninetyDayCombinedThreshold,
      ninetyDayCombinedMaxPct: g.ninetyDayCombinedMaxPct,
    },
    citation: citationOf({ ...g, citation: g.citation }),
  };
}

/**
 * The two SBA exposure ceilings: total gross loans within 90 days, and total
 * SBA guaranty across all approved loans — which counts 504 guarantees toward
 * the 7(a) ceiling.
 */
export function exposureCaps(): Cited<{
  maxGrossLoanAmount: number;
  maxTotalSbaGuarantyIncluding504: number;
}> {
  const c = DATA.exposureCaps;
  return {
    value: {
      maxGrossLoanAmount: c.maxGrossLoanAmount,
      maxTotalSbaGuarantyIncluding504: c.maxTotalSbaGuarantyIncluding504,
    },
    citation: citationOf({ ...c, citation: c.citation }),
  };
}

function tierPct(
  tiers: Array<{ maxGrossAmount: number | null; pct: number }>,
  grossAmount: number,
  what: string,
): number {
  for (const tier of tiers) {
    if (tier.maxGrossAmount === null || grossAmount <= tier.maxGrossAmount) return tier.pct;
  }
  const top = tiers[tiers.length - 1];
  throw new Error(
    `$${Math.round(grossAmount).toLocaleString('en-US')} is above the maximum ${what} loan amount` +
      `${top?.maxGrossAmount ? ` of $${top.maxGrossAmount.toLocaleString('en-US')}` : ''}.`,
  );
}

/**
 * The maximum guaranty percentage for a gross amount and loan type, from
 * SOP 50 10 8.1 Appendix 16.
 *
 * Export Express is NOT SBA Express: it carries 90% to $350,000 and 75% above,
 * where Express is a flat 50%. EWCP and International Trade are both 90%.
 */
export function guarantyPctFor(grossAmount: number, loanType: LoanType): number {
  const { value } = guarantyPctParams();
  if (loanType === 'express') return value.express;
  if (loanType === 'export-express') {
    return tierPct(value.exportExpress, grossAmount, 'Export Express');
  }
  if (loanType === 'international-trade') return value.internationalTrade;
  if (loanType === 'ewcp') return value.ewcp;
  return tierPct(value.standard, grossAmount, '7(a)');
}

/** The ceiling on SBA's total guaranty exposure for this loan type. */
export function maxGuarantyAmountFor(loanType: LoanType): number {
  const { value } = guarantyPctParams();
  return loanType === 'ewcp' || loanType === 'international-trade'
    ? value.exportMaxGuarantyAmount
    : value.maxGuarantyAmount;
}

/**
 * The guaranty percentage when other 7(a) loans fall inside the 90-day window.
 *
 * 13 CFR 120.210: the gross amounts combine, and once the combined amount
 * exceeds $150,000 the guaranty across the combined loans may not exceed 75%.
 * The later loan absorbs the reduction, so its own percentage can fall well
 * below 75% — in SBA's worked example, to 47%.
 */
export function guarantyPctWithPriors(args: {
  grossAmount: number;
  loanType: LoanType;
  /** Gross amounts of 7(a) loans approved to the applicant or affiliates within 90 days. */
  priorGross: number;
  /** Guaranteed dollars already committed on those loans. */
  priorGuaranteed: number;
}): number {
  const { value } = guarantyPctParams();
  const ownMax = guarantyPctFor(args.grossAmount, args.loanType);
  if (args.priorGross <= 0) return ownMax;

  const combinedGross = args.grossAmount + args.priorGross;
  if (combinedGross <= value.ninetyDayCombinedThreshold) return ownMax;

  const combinedCap = combinedGross * value.ninetyDayCombinedMaxPct;
  const availableDollars = combinedCap - args.priorGuaranteed;
  if (availableDollars <= 0) return 0;

  const impliedPct = availableDollars / args.grossAmount;
  return Math.min(ownMax, Math.max(0, impliedPct));
}

/** Every fiscal-year entry, for a model-card style disclosure page. */
export function allSchedules(): RawSchedule[] {
  return DATA.fiscalYears;
}
